import {HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi} from '@angular/common/http';
import {APP_INITIALIZER, ApplicationConfig, importProvidersFrom,} from '@angular/core';
import {BrowserAnimationsModule, provideAnimations, provideNoopAnimations} from '@angular/platform-browser/animations';
import {PreloadAllModules, provideRouter, withInMemoryScrolling, withPreloading,} from '@angular/router';
import {appRoutes} from "./app.routs";
import {provideAppraiser} from "./@admin";
import {ConfirmationService, MessageService} from "primeng/api";
import {DialogService} from "primeng/dynamicdialog";
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalService
} from "@azure/msal-angular";
import {environment} from "../environments/environment";
import {
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication,
  LogLevel,
  PublicClientApplication
} from "@azure/msal-browser";
import {BrowserModule} from "@angular/platform-browser";
import {provideAuth} from "./core/auth/auth.provider";
import {CurrencySymbolPipe} from "./@admin/pipes";
import {DatePipe} from "@angular/common";
import { AppConfigurationService } from './core/appConfiguration/app-configuration.service';

/**
 * App Config
 * */
export const appConfig: ApplicationConfig = {
  providers: [
    provideAnimations(),
    // Add Dependent Root Module Here
    provideRouter(
      appRoutes,
      withPreloading(PreloadAllModules),
      withInMemoryScrolling({scrollPositionRestoration: 'enabled'})
    ),
    {
      provide: APP_INITIALIZER,
      useFactory: (appInitializer: AppConfigurationService): any => appInitializer.getConfig(),
      // useFactory: loadConfig,
      deps: [AppConfigurationService],
      multi: true,
    },
    provideNoopAnimations(),
    // Add Dependent Root Module Here
    importProvidersFrom(BrowserModule, BrowserAnimationsModule),
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    provideAuth(),
    provideAppraiser(),
    MessageService,
    DialogService,
    ConfirmationService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    CurrencySymbolPipe,
    DatePipe,
  ],
};

/**
 * MSAL Instance Factory
 * */
export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.azureAuthConfig.auth.clientId,
      authority: environment.azureAuthConfig.auth.authority,
      redirectUri: AzureAuthRedirectUrls.postLogInRedirectUri,
      postLogoutRedirectUri: AzureAuthRedirectUrls.postLogoutRedirectUri
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage
    },
    system: {
      allowNativeBroker: false,
      loggerOptions: {
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false
      }
    }
  });
}

/**
 * MSAL Interceptor
 * */
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap: Map<string, string[]> = new Map<string, Array<string>>([
      // Profile Url for get profile info
      [AzureAuthRedirectUrls.profileInfoUrl, ["user.read", "profile", environment.azureAuthConfig.scopes[0]]],
      // Add Here Specific API EndPoints Which is needs to authenticate.
      [environment.apiConfig.apiBaseUrl + "EntityManagement" + "/*", [environment.azureAuthConfig.scopes[0]]],
      [environment.apiConfig.apiBaseUrl + "managecountyportal" + "/*", [environment.azureAuthConfig.scopes[0]]],
      [environment.apiConfig.apiBaseUrl + "general" + "/*", [environment.azureAuthConfig.scopes[0]]],
      [environment.apiConfig.apiBaseUrl + "Utility" + "/*", [environment.azureAuthConfig.scopes[0]]],
      [environment.apiConfig.apiBaseUrl + "documentation" + "*", [environment.azureAuthConfig.scopes[0]]],
      [environment.apiConfig.apiBaseUrl + "users" + "*", [environment.azureAuthConfig.scopes[0]]],
    ]
  );
  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap
  };
}

/**
 * MSAL Guard Config
 * */
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...environment.azureAuthConfig.scopes]
    },
    loginFailedRoute: AzureAuthRedirectUrls.loginFailedRoute
  };
}

/**
 * MSAL Redirect URLs
 * */
export const AzureAuthRedirectUrls = {
  postLogInRedirectUri: window.origin + '/manage-portals',
  postLogoutRedirectUri: window.origin + '/auth/sign-in',
  loginFailedRoute: window.origin + '/login-failed',
  profileInfoUrl: 'https://graph.microsoft.com/v1.0/me'
}
